import { groupBy } from 'lodash';
import { TemplateStopType } from '../enums/TemplateStopType';
import { templateStopConnectionColor } from '../icon/iconService';

const getBoundingBox = (coordinates) => {
  const latMax = Math.max(
    ...coordinates.map((coordinateTuple) => coordinateTuple[0])
  );
  const latMin = Math.min(
    ...coordinates.map((coordinateTuple) => coordinateTuple[0])
  );
  const lngMax = Math.max(
    ...coordinates.map((coordinateTuple) => coordinateTuple[1])
  );
  const lngMin = Math.min(
    ...coordinates.map((coordinateTuple) => coordinateTuple[1])
  );
  if (Math.abs(latMin - latMax) < 0.001 || Math.abs(lngMin - lngMax) < 0.001) {
    return [
      [latMin - 0.001, lngMin - 0.001],
      [latMax + 0.001, lngMax + 0.001],
    ];
  }
  return [
    [latMin, lngMin],
    [latMax, lngMax],
  ];
};

const getBoundingBoxCoords = (polyLines, stops = [], microHubs = []) => {
  let coordinates = [];
  if (polyLines && polyLines.length) {
    coordinates = polyLines.map((line) => line.positions).flat();
  }
  if (stops && stops.length) {
    stops.forEach((stop) => {
      coordinates.push([stop.address.latitude, stop.address.longitude]);
    });
  }
  // Only use microhubs if no other way for getting a bounding box can be found
  if (coordinates.length <= 1 && microHubs?.length) {
    coordinates = coordinates.concat(
      microHubs.map((hub) => [hub.address.lat, hub.address.lng])
    );
  }
  return coordinates;
};

/*
Calculate a unique key for a stops position to find duplicates to display overlapping stops
In order to account for minor inaccuracies during geocoding (e.g. Schneeglöckchenstr. 20 vs. Schneeglöckchenstraße 20.)
we round coordinates to 4 decimal places before building the key.
 */
const coordsToKey = (latitude, longitude) => {
  const decimalPlaces = 6;
  const latRounded =
    Math.round(latitude * Math.pow(10, decimalPlaces)) /
    Math.pow(10, decimalPlaces);
  const lngRounded =
    Math.round(longitude * Math.pow(10, decimalPlaces)) /
    Math.pow(10, decimalPlaces);

  return `${latRounded}${lngRounded}`;
};

const filterToursForCoords = (tours) => {
  return tours
    .map((tour) => ({
      ...tour,
      stops: tour.stops
        .filter((stop) => stop.address.latitude && stop.address.longitude)
        .map((stop) => ({
          ...stop,
          key: coordsToKey(stop.address.latitude, stop.address.longitude),
        }))
        .sort((s1, s2) =>
          s1.stopNumber > s2.stopNumber
            ? 1
            : s2.stopNumber > s1.stopNumber
            ? -1
            : 0
        ),
    }))
    .filter((tour) => tour.stops.length > 0);
};

const filterTemplateStopInstanceToursForCoords = (tours) => {
  return tours
    .map((tour) => ({
      ...tour,
      stops: tour.templateStopInstances
        .filter((stop) => stop.address.latitude && stop.address.longitude)
        .map((stop) => ({
          ...stop,
          key: coordsToKey(stop.address.latitude, stop.address.longitude),
        }))
        .sort((s1, s2) =>
          s1.stopNumber > s2.stopNumber
            ? 1
            : s2.stopNumber > s1.stopNumber
            ? -1
            : 0
        ),
    }))
    .filter((tour) => tour.stops.length > 0);
};

const filterMicroHubsForCoords = (microHubs) => {
  return microHubs.filter((hub) => hub.address.lng && hub.address.lat);
};

const filterStopsForCoords = (stops) => {
  return stops
    .filter((stop) => stop.address.latitude && stop.address.longitude)
    .map((stop) => ({
      ...stop,
      key: coordsToKey(stop.address.latitude, stop.address.longitude),
    }));
};

// This function is for template Stops from the templateStopService
const filterTemplateStopsForCoords = (templateStops) => {
  return templateStops
    .filter((stop) => stop.address?.latitude && stop.address?.longitude)
    .map((stop) => ({
      ...stop,
      key: coordsToKey(stop.address?.latitude, stop.address?.longitude),
    }));
};

const getPolyLines = (tours, microHubs = []) => {
  return tours.map((tour) => {
    const stopCoords = tour.stops.map((stop) => [
      stop.address.latitude,
      stop.address.longitude,
    ]);
    const matchingHubs = microHubs.filter(
      (hub) => tour.microHubName === hub.name
    );
    if (matchingHubs.length > 0) {
      const matchingHub = matchingHubs[0];
      stopCoords.unshift([matchingHub.address.lat, matchingHub.address.lng]);
      stopCoords.push([matchingHub.address.lat, matchingHub.address.lng]);
    }
    return { color: tour.color, positions: stopCoords };
  });
};

const filterTemplatesForCoords = (templates) => {
  return templates
    .map((template) => ({
      ...template,
      stops: template.stops
        .filter((stop) => stop.customer?.latitude && stop.customer?.longitude)
        .map((stop) => ({
          ...stop,
          key: coordsToKey(stop.customer.latitude, stop.customer.longitude),
        }))
        .sort((s1, s2) =>
          s1.stopNumber > s2.stopNumber
            ? 1
            : s2.stopNumber > s1.stopNumber
            ? -1
            : 0
        ),
    }))
    .filter((tour) => tour.stops.length > 0);
};

const filterTemplateStopTemplatesForCoords = (templates) => {
  return templates
    .map((template) => ({
      ...template,
      stops: template.stops
        .filter((stop) => stop.address?.latitude && stop.address?.longitude)
        .map((stop) => ({
          ...stop,
          key: coordsToKey(stop.address.latitude, stop.address.longitude),
        })),
    }))
    .filter((template) => template.stops.length > 0);
};

const getPolyLinesFromTemplates = (templates, microHubs = []) => {
  return templates.map((template) => {
    const stopCoords = template.stops.map((stop) => [
      stop.customer.latitude,
      stop.customer.longitude,
    ]);
    const matchingHubs = microHubs.filter(
      (hub) => template.microHub === hub.name
    );
    if (matchingHubs.length > 0) {
      const matchingHub = matchingHubs[0];
      stopCoords.unshift([matchingHub.address.lat, matchingHub.address.lng]);
      stopCoords.push([matchingHub.address.lat, matchingHub.address.lng]);
    }
    return { color: template.color, positions: stopCoords };
  });
};

// This function is for template Stops from the templateStopService
const getPolyLinesFromTemplateStopTemplates = (templates) => {
  return templates.map((template) => {
    const templateStopCoords = template.stops.map((templateStop) => [
      templateStop.address.latitude,
      templateStop.address.longitude,
    ]);
    return {
      color: template.color,
      positions: templateStopCoords,
    };
  });
};

// This function is for template Stops from the templateStopService
const getPolyLinesFromTemplateStop = (templateStops, selectedTemplateStop) => {
  // Draw lines only from pickup Stops
  if (selectedTemplateStop.stopType === TemplateStopType.Pickup) {
    const deliveryStops = templateStops.filter(
      (tes) => tes.stopType === TemplateStopType.Delivery
    );
    const deliveryStopLookup = deliveryStops.reduce((acc, deliveryStop) => {
      acc[deliveryStop.id] = deliveryStop;
      return acc;
    }, {});
    const polylines = [];
    // for each pickup stop find all relevant delivery stops
    const selectedPickupStopCoords = [
      selectedTemplateStop.address.latitude,
      selectedTemplateStop.address.longitude,
    ];
    const relatedDeliveryStopIds =
      selectedTemplateStop.pickupDeliveryPickup.map((x) => x.deliveryStopId);
    const deliveryStopCoords = relatedDeliveryStopIds.map((id) => {
      const deliveryStop = deliveryStopLookup[id];
      return deliveryStop
        ? [deliveryStop.address.latitude, deliveryStop.address.longitude]
        : [];
    });
    // for each pickup stop delivers stop pair add a polyline
    deliveryStopCoords
      .filter((c) => c.length > 0)
      .forEach((deliveryStopCoords) => {
        polylines.push({
          color: templateStopConnectionColor,
          positions: [selectedPickupStopCoords, deliveryStopCoords],
        });
      });

    return polylines;
  } else {
    const pickupStops = templateStops.filter(
      (ts) => ts.stopType === TemplateStopType.Pickup
    );
    const pickupStopLookup = pickupStops.reduce((acc, pickupStop) => {
      acc[pickupStop.id] = pickupStop;
      return acc;
    }, {});
    const polylines = [];
    // for each delivery stop find all relevant pickup stops
    const selectedDeliveryStopCoords = [
      selectedTemplateStop.address.latitude,
      selectedTemplateStop.address.longitude,
    ];
    const relatedPickupStopIds =
      selectedTemplateStop.pickupDeliveryDelivery.map((x) => x.pickupStopId);
    const pickupStopCoords = relatedPickupStopIds.map((id) => {
      const pickupStop = pickupStopLookup[id];
      return pickupStop
        ? [pickupStop.address.latitude, pickupStop.address.longitude]
        : [];
    });
    // for each pickup stop delivers stop pair add a polyline
    pickupStopCoords
      .filter((c) => c.length > 0)
      .forEach((pickupStopCoords) => {
        polylines.push({
          color: templateStopConnectionColor,
          positions: [selectedDeliveryStopCoords, pickupStopCoords],
        });
      });
    return polylines;
  }
};

// This function is for template Stops from the templateStopService
const getPolyLinesFromTemplateStops = (
  templateStops,
  isTemplateStopInstances
) => {
  // Draw lines only form pickup Stops
  const pickupStops = templateStops.filter(
    (ts) => ts.stopType === TemplateStopType.Pickup
  );
  const deliveryStops = templateStops.filter(
    (tes) => tes.stopType === TemplateStopType.Delivery
  );
  const deliveryStopLookup = deliveryStops.reduce((acc, deliveryStop) => {
    acc[deliveryStop.id] = deliveryStop;
    return acc;
  }, {});

  const polilines = [];
  // for each pickup stop find all relevant delivery stops
  pickupStops.forEach((pickupStop) => {
    const pickupStopCoords = [
      pickupStop.address.latitude,
      pickupStop.address.longitude,
    ];
    const relatedDeliveryStopIds = pickupStop.pickupDeliveryPickup.map((x) =>
      isTemplateStopInstances ? x.deliveryStopInstanceId : x.deliveryStopId
    );
    const deliveryStopCoords = relatedDeliveryStopIds.map((id) => {
      const deliveryStop = deliveryStopLookup[id];
      return deliveryStop
        ? [deliveryStop.address.latitude, deliveryStop.address.longitude]
        : [];
    });
    // for each pickup stop delivers stop pair add a polyline
    deliveryStopCoords
      .filter((c) => c.length > 0)
      .forEach((deliveryStopCoords) => {
        polilines.push({
          color: templateStopConnectionColor,
          positions: [pickupStopCoords, deliveryStopCoords],
        });
      });
  });
  return polilines;
};

const markerTypes = {
  stop: 'stop',
  tour: 'tour',
  toAddStop: 'toAddStop',
  template: 'template',
  templateStop: 'templateStop',
};

const getElementsWithSamePosition = (tours, stops, stopsToAdd = []) => {
  let allElements = tours
    .map((tour) =>
      tour.stops.map((stop) => ({
        key: stop.key,
        type: markerTypes.tour,
        tour: tour,
        stop: stop,
      }))
    )
    .flat()
    .concat(
      stops.map((stop) => ({
        key: stop.key,
        type: markerTypes.stop,
        stop: stop,
      }))
    )
    .concat(
      stopsToAdd.map((stop) => ({
        key: stop.key,
        type: markerTypes.toAddStop,
        stop: stop,
      }))
    );

  const groups = groupBy(allElements, (x) => x.key);
  return Object.getOwnPropertyNames(groups)
    .filter((key) => groups[key].length > 1)
    .map((key) => groups[key]);
};

// This function is for template Stops from the templateStopService
const getElementsWithSamePositionTemplateStop = (
  templates,
  unassignedTemplateStops = [],
  addedTemplateStops = [],
  selectedTemplateStopIds = []
) => {
  let allElements = templates
    .map((template) =>
      template.stops.map((templateStop, index) => ({
        key: templateStop.key,
        type: markerTypes.template,
        template: template,
        templateStop: templateStop,
        stopNumber: index,
        selected: selectedTemplateStopIds.includes(templateStop.id),
      }))
    )
    .flat()
    .concat(
      unassignedTemplateStops
        .map((templateStop) => ({
          key: templateStop.key,
          type: markerTypes.templateStop,
          templateStop: templateStop,
          selected: selectedTemplateStopIds.includes(templateStop.id),
        }))
        .concat(
          addedTemplateStops.map((templateStop) => ({
            key: templateStop.key,
            type: markerTypes.toAddStop,
            templateStop: templateStop,
            selected: selectedTemplateStopIds.includes(templateStop.id),
          }))
        )
    );

  const groups = groupBy(allElements, (x) => x.key);
  return Object.getOwnPropertyNames(groups)
    .filter((key) => groups[key].length > 1)
    .map((key) => groups[key]);
};

const getPolyLinesWithSamePosition = (polyLines) => {
  let allElements = polyLines
    .map((polyLine) => ({
      key: polyLine.key,
      polyLine: polyLine,
    }))
    .flat();
  const groups = groupBy(allElements, (x) => x.key);
  return Object.getOwnPropertyNames(groups)
    .filter((key) => groups[key].length > 1)
    .map((key) => groups[key]);
};

const filterToursForDuplicate = (tours, duplicateKeys) => {
  return tours.map((tour) => ({
    ...tour,
    stops: tour.stops.filter((s) => !duplicateKeys.some((x) => x === s.key)),
  }));
};

// This function is for template Stops from the templateStopService
const filterTemplatesForDuplicate = (templates, duplicateKeys) => {
  return templates.map((template) => ({
    ...template,
    stops: template.stops.filter(
      (s) => !duplicateKeys.some((x) => x === s.key)
    ),
  }));
};

const filterPolyLinesForDuplicate = (polyLines, duplicateKeys) => {
  return polyLines.filter(
    (polyLine) => !duplicateKeys.some((key) => key === polyLine.key)
  );
};

const filterStopsForDuplicate = (stops, duplicateKeys) => {
  return stops.filter((s) => !duplicateKeys.some((x) => x === s.key));
};

// This function is for template Stops from the templateStopService
const filterTemplateStopsForDuplicate = (templateStops, duplicateKeys) => {
  return templateStops.filter((s) => !duplicateKeys.some((x) => x === s.key));
};

const getNumStopsOnMap = (tours, stops, stopsToAdd = []) => {
  const numStopsOnTour = tours.reduce((a, t) => a + t.stops.length, 0);
  return numStopsOnTour + stops.length + stopsToAdd.length;
};

// This function is for template Stops from the templateStopService
const getNumTemplateStopsOnMap = (
  templates,
  templateStops,
  templateStopsToAdd = []
) => {
  const numTemplateStopsOnTemplate = templates.reduce(
    (a, t) => a + t.stops.length,
    0
  );
  return (
    numTemplateStopsOnTemplate +
    templateStops.length +
    templateStopsToAdd.length
  );
};

export {
  getBoundingBox,
  getBoundingBoxCoords,
  filterToursForCoords,
  filterMicroHubsForCoords,
  filterStopsForCoords,
  filterTemplateStopsForCoords,
  getPolyLines,
  filterTemplatesForCoords,
  filterTemplateStopTemplatesForCoords,
  getPolyLinesFromTemplates,
  getPolyLinesFromTemplateStop,
  getPolyLinesFromTemplateStopTemplates,
  markerTypes,
  getElementsWithSamePosition,
  getElementsWithSamePositionTemplateStop,
  getPolyLinesWithSamePosition,
  filterStopsForDuplicate,
  filterTemplateStopsForDuplicate,
  filterToursForDuplicate,
  filterTemplatesForDuplicate,
  getNumStopsOnMap,
  getNumTemplateStopsOnMap,
  getPolyLinesFromTemplateStops,
  filterTemplateStopInstanceToursForCoords,
  filterPolyLinesForDuplicate,
};
